import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import accountService from '../services/accountService';
import './UserAvatar.css';

/**
 * Erzeugt die Initialen des Benutzers aus Vor- und Nachname.
 * Fallback: erster Buchstabe des Benutzernamens oder 'U'.
 */
const getInitials = (user) => {
  const first = user.firstName?.trim()[0]?.toUpperCase() || '';
  const last = user.lastName?.trim()[0]?.toUpperCase() || '';
  return first + last || user.username?.[0]?.toUpperCase() || 'U';
};

/**
 * UserAvatar - Zeigt einen Avatar-Button mit Initialen oder Profilbild und ein
 * Dropdown-Menü mit Mein-Konto-, Feedback- und Abmelden-Optionen.
 *
 * @param {Object}   user              - Benutzerobjekt mit firstName, lastName, username, profilePictureUrl
 * @param {Function} onFeedbackClick   - Callback beim Klick auf "Feedback"
 * @param {Function} onAccountClick    - Callback beim Klick auf "Mein Konto"
 * @param {Function} onUsersClick      - Callback beim Klick auf "Benutzer"
 * @param {Function} onLandlordClick      - Callback beim Klick auf "Vermieterdaten"
 * @param {Function} onLoansClick         - Callback beim Klick auf "Darlehen"
 * @param {Function} onBankAccountsClick  - Callback beim Klick auf "Bankverbindungen"
 * @param {Function} onLogout             - Callback beim Klick auf "Abmelden"
 */
const UserAvatar = ({ user, onFeedbackClick, onAccountClick, onUsersClick, onLandlordClick, onLoansClick, onBankAccountsClick, onLogout, isRenterOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarImageUrl, setAvatarImageUrl] = useState(null); // authenticated blob URL

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const accountRef = useRef(null);
  const usersRef = useRef(null);
  const landlordRef = useRef(null);
  const loansRef = useRef(null);
  const bankAccountsRef = useRef(null);
  const feedbackRef = useRef(null);
  const logoutRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Load the profile picture via authenticated fetch whenever the user changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const loadImage = async () => {
      if (!user?.profilePictureUrl) {
        if (!cancelled) setAvatarImageUrl(null);
        return;
      }

      const token = authService.getToken();
      const url = `${accountService.getProfilePictureUrl()}?t=${Date.now()}`;

      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok || cancelled) {
          if (!cancelled) setAvatarImageUrl(null);
          return;
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setAvatarImageUrl(objectUrl);
      } catch {
        if (!cancelled) setAvatarImageUrl(null);
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user?.profilePictureUrl]);

  // ---------------------------------------------------------------------------
  // Dropdown toggle & menu handlers
  // ---------------------------------------------------------------------------
  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleAccount = () => {
    setIsOpen(false);
    onAccountClick?.();
  };

  const handleUsers = () => {
    setIsOpen(false);
    onUsersClick?.();
  };

  const handleLandlord = () => {
    setIsOpen(false);
    onLandlordClick?.();
  };

  const handleLoans = () => {
    setIsOpen(false);
    onLoansClick?.();
  };

  const handleBankAccounts = () => {
    setIsOpen(false);
    onBankAccountsClick?.();
  };


  const handleFeedback = () => {
    setIsOpen(false);
    onFeedbackClick?.();
  };

  const handleLogout = () => {
    setIsOpen(false);
    onLogout?.();
  };

  // Focus first menu item when dropdown opens
  useEffect(() => {
    if (isOpen) {
      accountRef.current?.focus();
    }
  }, [isOpen]);

  // Arrow key navigation inside the dropdown menu
  const handleMenuKeyDown = useCallback((e) => {
    const items = [
      accountRef.current,
      usersRef.current,
      landlordRef.current,
      loansRef.current,
      bankAccountsRef.current,
      feedbackRef.current,
      logoutRef.current
    ].filter(Boolean);

    const focused = document.activeElement;
    const index = items.indexOf(focused);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(index + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(index - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      // Tab schließt das Menü, ohne den natürlichen Fokusfluss zu blockieren (WCAG 2.1.1)
      setIsOpen(false);
    }
  }, []);

  // Click outside and Escape key handler (consolidated)
  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentEvent = (e) => {
      if (
        e.type === 'mousedown' &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
      if (e.type === 'keydown' && e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleDocumentEvent);
    document.addEventListener('keydown', handleDocumentEvent);
    return () => {
      document.removeEventListener('mousedown', handleDocumentEvent);
      document.removeEventListener('keydown', handleDocumentEvent);
    };
  }, [isOpen]);

  if (!user) return null;

  return (
    <div className="user-avatar" id="nav-user-avatar" ref={containerRef}>
      <button
        type="button"
        className="user-avatar__trigger"
        id="nav-user-avatar-button"
        ref={triggerRef}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`Benutzermenü für ${user.firstName || user.username || 'Benutzer'}`}
        title={`${user.firstName || user.username || 'Benutzer'}`}
      >
        <div className={`user-avatar__image${avatarImageUrl ? ' user-avatar__image--has-picture' : ''}`} id="nav-user-avatar-image">
          {avatarImageUrl ? (
            <img
              src={avatarImageUrl}
              alt={`Profilbild von ${user.firstName || user.username || 'Benutzer'}`}
              onError={() => setAvatarImageUrl(null)}
            />
          ) : (
            <span aria-hidden="true">{getInitials(user)}</span>
          )}
        </div>
        <span className="user-avatar__name" id="nav-user-avatar-name">
          {user.firstName || user.username || 'User'}
        </span>
        <svg
          className="user-avatar__chevron"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="user-avatar__dropdown"
          id="nav-user-avatar-dropdown"
          role="menu"
          aria-labelledby="nav-user-avatar-button"
          onKeyDown={handleMenuKeyDown}
        >
          <button
            type="button"
            className="user-avatar__menu-item"
            id="nav-user-avatar-account"
            ref={accountRef}
            onClick={handleAccount}
            role="menuitem"
          >
            <i className="bi bi-person-circle" aria-hidden="true"></i>
            Mein Konto
          </button>
          {!isRenterOnly && (
            <button
              type="button"
              className="user-avatar__menu-item"
              id="nav-user-avatar-users"
              ref={usersRef}
              onClick={handleUsers}
              role="menuitem"
            >
              <i className="bi bi-people" aria-hidden="true"></i>
              Benutzer
            </button>
          )}
          {!isRenterOnly && (
            <button
              type="button"
              className="user-avatar__menu-item"
              id="nav-user-avatar-landlord"
              ref={landlordRef}
              onClick={handleLandlord}
              role="menuitem"
            >
              <i className="bi bi-house-door" aria-hidden="true"></i>
              Vermieterdaten
            </button>
          )}
          {!isRenterOnly && (
            <button
              type="button"
              className="user-avatar__menu-item"
              id="nav-user-avatar-loans"
              ref={loansRef}
              onClick={handleLoans}
              role="menuitem"
            >
              <i className="bi bi-bank" aria-hidden="true"></i>
              Darlehen
            </button>
          )}
          {!isRenterOnly && (
            <button
              type="button"
              className="user-avatar__menu-item"
              id="nav-user-avatar-bank-accounts"
              ref={bankAccountsRef}
              onClick={handleBankAccounts}
              role="menuitem"
            >
              <i className="bi bi-credit-card-2-front" aria-hidden="true"></i>
              Bankverbindungen
            </button>
          )}
          <button
            type="button"
            className="user-avatar__menu-item"
            id="nav-user-avatar-feedback"
            ref={feedbackRef}
            onClick={handleFeedback}
            role="menuitem"
          >
            <i className="bi bi-chat-dots" aria-hidden="true"></i>
            Feedback
          </button>
          <div className="user-avatar__divider" role="separator" aria-hidden="true"></div>
          <button
            type="button"
            className="user-avatar__menu-item"
            id="nav-user-avatar-logout"
            ref={logoutRef}
            onClick={handleLogout}
            role="menuitem"
          >
            <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
};

UserAvatar.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    profilePictureUrl: PropTypes.string
  }).isRequired,
  onFeedbackClick: PropTypes.func,
  onAccountClick: PropTypes.func,
  onUsersClick: PropTypes.func,
  onLandlordClick: PropTypes.func,
  onLoansClick: PropTypes.func,
  onBankAccountsClick: PropTypes.func,
  onLogout: PropTypes.func,
  /** Wenn true werden Vermieter-exklusive Menüpunkte ausgeblendet */
  isRenterOnly: PropTypes.bool
};

export default UserAvatar;
